import React, { useEffect, useState } from "react";
import { Modal, Form, Radio, Select, Alert, Space } from "antd";
import { useTranslate, useApiUrl } from "@pankod/refine-core";
import axios from "axios";
import { ILLMModel, IVerificationJob } from "interfaces";

interface TriggerModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  jobs: IVerificationJob[];
}

const TriggerModal: React.FC<TriggerModalProps> = ({
  visible,
  onClose,
  onSuccess,
  jobs,
}) => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const APIToken = localStorage.getItem("api-token");

  const [form] = Form.useForm();
  const [models, setModels] = useState<ILLMModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [conflictJobId, setConflictJobId] = useState<string | null>(null);

  const scope = Form.useWatch("scope", form);

  const retryableJobs = jobs.filter(
    (j) => j.status === "partial" || j.status === "failed"
  );

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setConflictJobId(null);
      setLoading(true);
      axios
        .get(`${apiUrl}/llm-models`, {
          headers: { Authorization: `Bearer ${APIToken}` },
        })
        .then((res) => setModels(res.data || []))
        .catch(() => setModels([]))
        .finally(() => setLoading(false));
    }
  }, [visible, apiUrl, APIToken, form]);

  const providers = Array.from(new Set(models.map((m) => m.provider)));

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      setConflictJobId(null);

      const payload: Record<string, string> = {
        scope: values.scope,
        model_id: values.model_id,
      };
      if (values.scope === "retry" && values.parent_job_id) {
        payload.parent_job_id = values.parent_job_id;
      }

      await axios.post(`${apiUrl}/verification-jobs`, payload, {
        headers: { Authorization: `Bearer ${APIToken}` },
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const activeId = error.response?.data?.job_id;
        setConflictJobId(activeId || "unknown");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t("verification.trigger.title")}
      visible={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={submitting}
      okText={t("verification.trigger.submit")}
      cancelText={t("verification.trigger.cancel")}
    >
      {conflictJobId && (
        <Alert
          type="warning"
          message={t("verification.jobs.job_running")}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Form form={form} layout="vertical" initialValues={{ scope: "all" }}>
        <Form.Item
          name="scope"
          label={t("verification.trigger.scope_label")}
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="all">
                {t("verification.trigger.scope_all")}
              </Radio>
              <Radio value="unverified">
                {t("verification.trigger.scope_unverified")}
              </Radio>
              <Radio value="retry">
                {t("verification.trigger.scope_retry")}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {scope === "retry" && (
          <Form.Item
            name="parent_job_id"
            label={t("verification.trigger.select_parent_job")}
            rules={[{ required: true }]}
          >
            <Select placeholder={t("verification.trigger.select_parent_job")}>
              {retryableJobs.map((job) => (
                <Select.Option key={job.id} value={job.id}>
                  {new Date(job.created_at).toLocaleDateString()} —{" "}
                  {job.model_name} — {job.total_items} items ({job.status})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="model_id"
          label={t("verification.trigger.model_label")}
          rules={[{ required: true }]}
        >
          <Select loading={loading} placeholder={t("verification.trigger.model_label")}>
            {providers.map((provider) => (
              <Select.OptGroup key={provider} label={provider.toUpperCase()}>
                {models
                  .filter((m) => m.provider === provider)
                  .map((m) => (
                    <Select.Option
                      key={m.id}
                      value={m.id}
                      disabled={!m.is_active}
                    >
                      {m.name}
                    </Select.Option>
                  ))}
              </Select.OptGroup>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TriggerModal;
